import { Injectable } from '@angular/core';
import { CellValue, SimpleCellAddress } from 'hyperformula';
import { EXCEL_FILE_STANDARD_DATA } from '../../constants';
import { HyperFormulaDataService } from '../hyper-formula-data/hyper-formula-data.service';

export interface ResultRow {
  categoryName: string | undefined;

  subcatTitleColspan: string | undefined;
  subcatTitleRowspan: string | undefined;
  subcatImgFileName: string | undefined;
  subcatName: string | undefined;
  subcatValueTimeSeriesGraphStyle: string | undefined;
  subcatValueTimeSeriesGraphIndex: string | undefined;
  subcatValueTimeSeriesGraphColspan: string | undefined;
  subcatValueTimeSeriesGraphTitle: string | undefined;
  subcatValueTimeSeriesGraphInfo: string | undefined;

  subcatValueName: string | undefined;
  subcatValueHierarchy: string | undefined;
  subcatValueUnit: string | undefined;
  subcatValueY1: string | undefined;
  subcatValueY2: string | undefined;
  subcatValueY3: string | undefined;
  subcatValueY4: string | undefined;
  subcatValueY5: string | undefined;

  subcatValuePieChartTitle: string | undefined;
  subcatValuePieChartSubtitle: string | undefined;
  subcatValuePieChartIndex: string | undefined;
  subcatValuePieChartColspan: string | undefined;
  subcatValuePieChartInfo: string | undefined;

  subcatValueDataPointCardTitle: string | undefined;
  subcatValueDataPointCardSubtitle: string | undefined;
  subcatValueDataPointCardIndex: string | undefined;
  subcatValueDataPointCardColspan: string | undefined;
  subcatValueDataPointCardImgFileName: string | undefined;
  subcatValueDataPointCardInfo: string | undefined;
}

export interface ScenarioRow {
  category: string | undefined;
  name: string;
  options: string[] | undefined;
  defaultValue: string | undefined;
  unit: string | undefined;
  modifiable: boolean;
  comment: string | undefined;
}

export interface CarePathwayRow {
  stepTitle: string | undefined;
  stepSubtitle: string | undefined;
  stepMatIconName: string | undefined;
  stepDescription: string | undefined;

  impactCategoryName: string | undefined;

  impactCardIndex: string | undefined;
  impactName: string | undefined;
  impactDescription: string | undefined;
  impactImageFileName: string | undefined;
  impactCardColspan: string | undefined;
  impactCardAccent: string | undefined;

  subImpact1Name: string | undefined;
  subImpact1Value: string | undefined;
  subImpact1Unit: string | undefined;
  subImpact1AddPieChart: string | undefined;

  subImpact2Name: string | undefined;
  subImpact2Value: string | undefined;
  subImpact2Unit: string | undefined;
  subImpact2AddPieChart: string | undefined;
}

export interface KeyValueRow {
  key: string | undefined;
  value: string | undefined;
}

export interface SummaryRow {
  imageFileName: string | undefined;
  title: string | undefined;
  matIconName: string | undefined;
  htmlContent: string | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class StructuredInputService {
  private _resultRows: ResultRow[] = [];
  private _scenarioRows: ScenarioRow[] = [];
  private _carePathwayRows: CarePathwayRow[] = [];
  private _introRows: KeyValueRow[] = [];
  private _configRows: KeyValueRow[] = [];
  private _summaryRows: SummaryRow[] = [];
  private _scenarioDefaultValueColIndex = 3;

  constructor(private _hfDataService: HyperFormulaDataService) {}

  getResultRows() {
    return this._resultRows;
  }

  getScenarioRows() {
    return this._scenarioRows;
  }

  getCarePathwayRows() {
    return this._carePathwayRows;
  }

  getIntroRows() {
    return this._introRows;
  }

  getConfigRows() {
    return this._configRows;
  }

  getSummaryRows() {
    return this._summaryRows;
  }

  convertRowsToObjects(scenarioOnly = false): void {
    const allSheetValues = this._hfDataService.instance.getAllSheetsValues();
    if (!scenarioOnly) {
      this._convertToResultRows(
        allSheetValues[EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.RESULTS],
      );

      this._convertToCarePathwayRows(
        allSheetValues[EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CARE_PATHWAY],
      );
    }

    this._convertToScenarioRows(
      allSheetValues[EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SCENARIO],
    );

    this._convertToIntroRows(
      allSheetValues[EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.INTRO],
    );

    this._convertToConfigRows(
      allSheetValues[EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.CONFIG],
    );

    this._convertToSummaryRows(
      allSheetValues[EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SUMMARY],
    );
  }

  updateScenarioRow(index: number, value: string): void {
    if (!this._scenarioRows[index]) {
      throw new Error();
    }

    // Do not overwrite rows that are not meant to be modified (they often contain formulas)
    if (!this._scenarioRows[index].modifiable) {
      return;
    }

    const sanitized = this._sanitizeScenarioValue(value);

    if (this._scenarioRows[index].defaultValue == sanitized) {
      return;
    }

    const cellAddress = {
      sheet: this._getScenarioSheetId(),
      col: this._scenarioDefaultValueColIndex,
      row: index + 1, // HyperFormula uses 1-based indexing for rows and 0-based indexing for columns.
    } as SimpleCellAddress;

    this._hfDataService.instance.setCellContents(cellAddress, [[sanitized]]);
    this._scenarioRows[index].defaultValue = this._hfDataService.instance
      .getCellValue(cellAddress)
      ?.toString();
  }

  private _convertToResultRows(rows: CellValue[][]): void {
    if (!rows) {
      return;
    }

    this._resultRows = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      // Only normalize structure-defining labels to avoid altering display content
      // like value names or rich text that may rely on whitespace.
      const categoryName = this._normalizeSectionLabel(row[0]);
      const subcatName = this._normalizeSectionLabel(row[1]);
      this._resultRows.push({
        categoryName,

        subcatName,
        subcatTitleColspan: row[2]?.toString(),
        subcatTitleRowspan: row[3]?.toString(),
        subcatImgFileName: row[4]?.toString(),

        subcatValueName: row[5]?.toString(),
        subcatValueHierarchy: row[6]?.toString(),
        subcatValueUnit: row[7]?.toString(),
        subcatValueY1: row[8]?.toString(),
        subcatValueY2: row[9]?.toString(),
        subcatValueY3: row[10]?.toString(),
        subcatValueY4: row[11]?.toString(),
        subcatValueY5: row[12]?.toString(),

        subcatValueTimeSeriesGraphTitle: row[13]?.toString(),
        subcatValueTimeSeriesGraphIndex: row[14]?.toString(),
        subcatValueTimeSeriesGraphColspan: row[15]?.toString(),
        subcatValueTimeSeriesGraphInfo: row[16]?.toString(),
        subcatValueTimeSeriesGraphStyle: row[17]?.toString(),

        subcatValuePieChartTitle: row[18]?.toString(),
        subcatValuePieChartSubtitle: row[19]?.toString(),
        subcatValuePieChartIndex: row[20]?.toString(),
        subcatValuePieChartColspan: row[21]?.toString(),
        subcatValuePieChartInfo: row[22]?.toString(),

        subcatValueDataPointCardTitle: row[23]?.toString(),
        subcatValueDataPointCardSubtitle: row[24]?.toString(),
        subcatValueDataPointCardIndex: row[25]?.toString(),
        subcatValueDataPointCardColspan: row[26]?.toString(),
        subcatValueDataPointCardImgFileName: row[27]?.toString(),
        subcatValueDataPointCardInfo: row[28]?.toString(),
      });
    }
  }

  private _convertToScenarioRows(rows: CellValue[][]): void {
    if (!rows) {
      return;
    }

    this._scenarioRows = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      this._scenarioRows.push({
        category: row[0]?.toString(),
        name: row[1]?.toString() ?? '',
        options: row[2]
          ?.toString()
          .split(',')
          .map((string) => string.trim()),
        defaultValue: row[this._scenarioDefaultValueColIndex]?.toString(),
        unit: row[4]?.toString(),
        modifiable: !!row[5],
        comment: row[6]?.toString(),
      });
    }
  }

  private _convertToCarePathwayRows(rows: CellValue[][]): void {
    if (!rows) {
      return;
    }

    this._carePathwayRows = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      this._carePathwayRows.push({
        stepTitle: row[0]?.toString(),
        stepSubtitle: row[1]?.toString(),
        stepMatIconName: row[2]?.toString(),
        stepDescription: row[3]?.toString(),
        impactCategoryName: row[4]?.toString(),
        impactCardIndex: row[5]?.toString(),
        impactName: row[6]?.toString(),
        impactDescription: row[7]?.toString(),
        impactImageFileName: row[8]?.toString(),
        impactCardColspan: row[9]?.toString(),
        impactCardAccent: row[10]?.toString(),
        subImpact1Name: row[11]?.toString(),
        subImpact1Value: row[12]?.toString(),
        subImpact1Unit: row[13]?.toString(),
        subImpact1AddPieChart: row[14]?.toString(),
        subImpact2Name: row[15]?.toString(),
        subImpact2Value: row[16]?.toString(),
        subImpact2Unit: row[17]?.toString(),
        subImpact2AddPieChart: row[18]?.toString(),
      });
    }
  }

  private _convertToIntroRows(rows: CellValue[][]): void {
    this._introRows = this._convertToKeyValueRows(rows);
  }

  private _convertToConfigRows(rows: CellValue[][]): void {
    this._configRows = this._convertToKeyValueRows(rows);
  }

  private _convertToKeyValueRows(rows: CellValue[][]) {
    if (!rows) return [];

    const outputRows = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      outputRows.push({
        key: row[0]?.toString(),
        value: row[1]?.toString(),
      });
    }

    return outputRows;
  }

  private _convertToSummaryRows(rows: CellValue[][]): void {
    if (!rows) {
      return;
    }

    this._summaryRows = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];

      let summaryRow = {
        imageFileName: row[0]?.toString(),
        title: row[1]?.toString(),
        matIconName: row[2]?.toString(),
        htmlContent: row[3]?.toString(),
      };

      this._summaryRows.push(summaryRow);
    }
  }

  private _normalizeSectionLabel(value: CellValue): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    const normalized = value.toString().trim();
    return normalized.length > 0 ? normalized : undefined;
  }

  private _getScenarioSheetId(): number {
    return this._hfDataService.sheetIdMapping.indexOf(
      EXCEL_FILE_STANDARD_DATA.SHEET_NAMES.SCENARIO,
    );
  }

  /**
   * Normalize user input so "1,2" or "1 234,56" gets parsed as a number
   * instead of a string, preventing #VALUE errors in HyperFormula.
   * Returns original value for non-numeric strings or formulas.
   */
  private _sanitizeScenarioValue(value: unknown): string | number | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const str = value.toString().trim();
    if (!str) return '';

    // Keep formulas or plain text intact
    if (str.startsWith('=')) return str;

    // Remove grouping spaces and normalize commas to dots
    const normalized = str
      .replace(/\s+/g, '')
      .replace(/\u00A0/g, '')
      .replace(/,/g, '.');
    const num = Number(normalized);

    return Number.isFinite(num) ? num : str;
  }
}
